import React from 'react';
import { useDroppable } from '@dnd-kit/core';
import { DAY_LABELS_SHORT } from '../utils/poa-helpers';
import Tooltip from '../components/ui/Tooltip';

interface Props {
  day: number;
  isToday: boolean;
  onClick?: () => void;
}

/**
 * Slim in-grid indicator that occupies the focused day's chronological
 * position between the six visible day cards. Uses CSS `writing-mode:
 * vertical-rl` to render the 3-letter day name (e.g. "WED") top-to-bottom
 * with each glyph upright, plus a thin vertical accent line above it.
 *
 * Wired as a useDroppable so dragging an item near the indicator drops
 * into the focused day's bucket (same id as the focus card above).
 */
const FocusGhost: React.FC<Props> = ({ day, isToday, onClick }) => {
  // Distinct droppable id from the focus DayColumn so @dnd-kit's `over`
  // resolution doesn't ambiguously pick between two same-id targets.
  // `data.day` is what the page's drag-end handler reads, so the
  // destination day still resolves correctly.
  const { setNodeRef, isOver } = useDroppable({
    id: `day-${day}-ghost`,
    data: { day, type: 'day' },
  });

  return (
    <Tooltip label={`${DAY_LABELS_SHORT[day]} featured above`}><button
      type="button"
      ref={setNodeRef}
      onClick={onClick}
      aria-label={`${DAY_LABELS_SHORT[day]} featured above — scroll to focus`}
      className={`group flex-shrink-0 self-stretch
                  flex flex-col items-center justify-center
                  w-6 rounded-md transition-all
                  bg-primary text-black
                  hover:brightness-110
                  ${isOver ? 'ring-2 ring-inset ring-primary/60 brightness-110' : ''}`}
    >
      {/* Vertical day-name text. writing-mode keeps each glyph upright. */}
      <div
        style={{ writingMode: 'vertical-rl' }}
        className="text-[10px] uppercase tracking-[0.18em] font-bold leading-none select-none"
      >
        {DAY_LABELS_SHORT[day].toUpperCase()}
      </div>
    </button></Tooltip>
  );
};

export default FocusGhost;
